import { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { NAV, type NavGroup, type NavKey, ROUTE_FOR } from '@/roles/sv/nav'
import { VOC_SUB_NAV, vocSectionFromSearch } from '@/features/voc/sections'
import { useLayoutStore } from '@/shared/store/layoutStore'
import { logoutLocally } from '@/features/auth/logout'
import { DrawerFrame, DrawerGroup, drawerItemClass } from '@/shared/shell/DrawerFrame'
import { cn } from '@/shared/utils/cn'
import { NavIcon } from '@/shared/ui'

const GROUP_LABEL: Record<NavGroup, string> = {
  home: '홈',
  qsc: 'QSC · 매장 품질관리',
  prm: 'PRM · 매장 운영관리',
  qa: 'QA · 납품업체 품질관리',
  as: 'AS',
  claim: '클레임 관리',
  more: '더보기',
}

interface DrawerProps {
  active?: NavKey
  onNavigate?: () => void
}

export function Drawer({ active, onNavigate }: DrawerProps) {
  const setOpen = useLayoutStore((state) => state.setDrawerOpen)
  const [vocExpanded, setVocExpanded] = useState(active === 'voc')
  const location = useLocation()
  const navigate = useNavigate()
  const visible = NAV
  const vocSection = vocSectionFromSearch(location.search)

  useEffect(() => {
    if (active === 'voc') setVocExpanded(true)
  }, [active])

  const toggleVocMenu = () => {
    const nextExpanded = active === 'voc' ? !vocExpanded : true
    setVocExpanded(nextExpanded)
    if (nextExpanded) {
      onNavigate?.()
      navigate(VOC_SUB_NAV[0].path)
    }
  }

  return (
    <DrawerFrame roleLabel="SV / 슈퍼바이저" onLogout={() => logoutLocally()}>
      {(['home', 'qsc', 'prm', 'qa', 'as', 'claim', 'more'] as NavGroup[]).map((group) => {
        const items = visible.filter((item) => item.group === group)
        if (items.length === 0) return null
        return (
          <DrawerGroup key={group} label={GROUP_LABEL[group]}>
            {items.map((item) => {
              if (item.k === 'voc') {
                const selected = active === item.k
                return (
                  <div key={item.k}>
                    <button
                      type="button"
                      className={drawerItemClass(selected)}
                      onClick={toggleVocMenu}
                      aria-expanded={vocExpanded}
                    >
                      <NavIcon name={item.icon} className="size-5 shrink-0" />
                      <span className="min-w-0 flex-1 text-left">{item.ko}</span>
                      <ChevronRight
                        aria-hidden
                        className={cn('size-4 shrink-0 transition', vocExpanded && 'rotate-90')}
                      />
                    </button>
                    <div
                      className={cn(
                        'grid transition-all duration-300 ease-out',
                        vocExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                      )}
                    >
                      <div className="overflow-hidden pl-8 pr-1 pt-1">
                        <div className="flex flex-col gap-1 border-l border-border-subtle pl-2">
                          {VOC_SUB_NAV.map((child) => (
                            <NavLink
                              key={child.k}
                              to={child.path}
                              onClick={() => {
                                onNavigate?.()
                                setOpen(false)
                              }}
                              className={cn(
                                'rounded-md px-3 py-2 text-preset-helper font-bold transition',
                                active === 'voc' && vocSection === child.k
                                  ? 'bg-brand text-white shadow-card'
                                  : 'text-text-muted hover:bg-gray-50 hover:text-text',
                              )}
                            >
                              {child.ko}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <NavLink
                  key={item.k}
                  to={ROUTE_FOR[item.k]}
                  onClick={() => {
                    onNavigate?.()
                    setOpen(false)
                  }}
                  className={({ isActive }) =>
                    drawerItemClass(active ? active === item.k : isActive)
                  }
                >
                  <NavIcon name={item.icon} className="size-5 shrink-0" />
                  <span>{item.ko}</span>
                </NavLink>
              )
            })}
          </DrawerGroup>
        )
      })}
    </DrawerFrame>
  )
}
